import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { APIWeb } from '@/api'
import RefreshList from '@/components/RefreshList'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { toFixedNoRound, formatThousands, formatDateTime } from '@/utils'

export default React.memo(function ExchangeRecord() {
  const { t } = useTranslation()
  const [data, setData] = useState<ModelsExchangeLog[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // 通用的数据加载函数
  const fetchExchangeRecord = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading) return

    setLoading(true)
    try {
      const result = await APIWeb.getExchangeRecord({
        page,
        page_size: pageSize
      })

      if (result?.list) {
        const newData = result.list

        if (isAppend) {
          // 追加数据（用于加载更多）
          setData(prevData => [...prevData, ...newData])
        } else {
          // 替换数据（用于初始加载和刷新）
          setData(newData)
        }

        // 检查是否还有更多数据
        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setHasMore(true)
          if (!isAppend) {
            setCurrentPage(2) // 初始加载后，下次从第2页开始
          } else {
            setCurrentPage(prev => prev + 1) // 加载更多时，页码递增
          }
        }
      } else {
        if (isAppend) {
          setHasMore(false)
        } else {
          setData([])
          setHasMore(false)
        }
      }
    } catch (error) {
      console.error('获取兑换记录失败:', error)
      setHasMore(false) // 发生错误时停止加载更多
    } finally {
      setLoading(false)
    }
  }, [loading])

  // 加载更多数据
  const loadMore = useCallback(async () => {
    await fetchExchangeRecord(currentPage, true)
  }, [fetchExchangeRecord, currentPage])

  // 下拉刷新处理函数
  const onRefresh = useCallback(async () => {
    await fetchExchangeRecord(1, false)
  }, [fetchExchangeRecord])

  // 渲染单个兑换记录
  const renderItem = useCallback((item: ModelsExchangeLog) => {
    return (
      <div className="px-6 py-4 border-b border-[#F5F5F5]">
        <div className="flex items-center justify-between mb-2">
          {/* 兑换方向 */}
          <div className="flex items-center">
            <span className="text-base font-medium text-black">
              {item.in_type?.toUpperCase()} → {item.out_type?.toUpperCase()}
            </span>
          </div>
          {/* 时间 */}
          <span className="text-3 text-[#979797]">
            {formatDateTime(item.created_at)}
          </span>
        </div>

        <div className="flex items-center justify-between">
          {/* 兑换金额 */}
          <div className="flex flex-col">
            <div className="text-3.5 text-[#979797] mb-1">
              {t('支付')}: {formatThousands(item.in_amount || 0, 2, true)} {item.in_type?.toUpperCase()}
            </div>
            <div className="text-3.5 text-black">
              {t('收到')}: {formatThousands(item.out_amount || 0, 2, true)} {item.out_type?.toUpperCase()}
            </div>
          </div>

          {/* 汇率和服务费 */}
          <div className="flex flex-col items-end">
            <div className="text-3 text-[#979797] mb-1">
              {t('汇率')}: {toFixedNoRound(item.exchange_rate || 1, 2)}
            </div>
            {item.service_fee && (
              <div className="text-3 text-[#979797] mt-1">
                {t('服务费')}: {formatThousands(item.service_fee, 2, true)} {item.in_type?.toUpperCase()}
              </div>
            )}
          </div>
        </div>

        {/* 备注 */}
        {item.remark && (
          <div className="mt-2 text-3 text-[#979797]">
            {t('备注')}: {item.remark}
          </div>
        )}
      </div>
    )
  }, [t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-3.75 font-medium text-black">{t('兑换记录')}</span>}
      />

      <div className="bg-white flex-1 flex flex-col">
        <RefreshList
          data={data}
          renderItem={renderItem}
          keyExtractor={(item, index) => `${item.id}-${index}`}
          onRefresh={onRefresh}
          onLoadMore={loadMore}
          loading={loading}
          hasMore={hasMore}
          className="flex-1"
          emptyText={t('暂无兑换记录')}
        />
      </div>
    </PageLayout>
  )
})
